import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebaseConfig/firebase";
import { ENDPOINTS, fetchEndpoint } from "../config/endpoints";

// Listar todos los usuarios (Auth + datos extra de Firestore, priorizando Auth)
export async function listarUsuarios() {
  try {
    // 1. Obtener usuarios de Auth vía endpoint
    const response = await fetchEndpoint(ENDPOINTS.USERS.LIST);
    const authUsers = response.users || [];

    // 2. Obtener datos extra de Firestore
    const usuariosRef = collection(db, "users");
    const snapshot = await getDocs(usuariosRef);
    const firestoreUsers = {};
    snapshot.docs.forEach(docSnap => {
      firestoreUsers[docSnap.id] = docSnap.data();
    });

    // 3. Combinar ambos conjuntos, priorizando Auth
    const usuariosCombinados = authUsers.map(user => {
      const extra = firestoreUsers[user.uid] || {};
      return {
        // Siempre de Auth
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        disabled: user.disabled,
        metadata: user.metadata,
        // Solo de Firestore si no existen en Auth
        nivelSuscripcion: extra.nivelSuscripcion || 0,
        roles: extra.roles || ['free'],
        isActive: typeof extra.isActive === 'boolean' ? extra.isActive : true,
        createdAt: extra.createdAt || null,
        lastLogin: extra.lastLogin || null,
        photoURL: extra.photoURL || '',
        extraData: extra.extraData || {}
      };
    });

    // Agregar campo de control para verificar la versión
    return {
      userDataVersion: 'v2-prioriza-auth',
      usuarios: usuariosCombinados
    };
  } catch (error) {
    console.error('Error al listar usuarios:', error);
    // Fallback a Firestore si el endpoint falla
    const usuariosRef = collection(db, "users");
    const snapshot = await getDocs(usuariosRef);
    return { userDataVersion: 'fallback-firestore', usuarios: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) };
  }
}

// Crear un usuario
export async function crearUsuario(data) {
  try {
    const response = await fetchEndpoint(ENDPOINTS.USERS.CREATE, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response;
  } catch (error) {
    console.error('Error al crear usuario:', error);
    // Fallback a Firestore si el endpoint falla
    let uid = data.uid;
    if (!uid) {
      const newDocRef = doc(collection(db, "users"));
      uid = newDocRef.id;
      data.uid = uid;
    }
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, data);
    return { uid, ...data };
  }
}

// Editar usuario
export async function editarUsuario(uid, data) {
  try {
    const response = await fetchEndpoint(ENDPOINTS.USERS.UPDATE, {
      method: 'PUT',
      body: JSON.stringify({ uid, ...data })
    });
    return response;
  } catch (error) {
    console.error('Error al editar usuario:', error);
    // Fallback a Firestore si el endpoint falla
    const userRef = doc(db, "users", uid);
    // Normalizar datos para evitar undefined
    const safeData = {
      ...data,
      displayName: data.displayName ?? "",
      email: data.email ?? "",
      nivelSuscripcion: data.nivelSuscripcion ?? 0,
      roles: data.roles ?? ['free'],
      isActive: typeof data.isActive === 'boolean' ? data.isActive : true,
      photoURL: data.photoURL ?? '',
      extraData: data.extraData ?? {},
      metadata: data.metadata ?? {},
      disabled: typeof data.disabled === 'boolean' ? data.disabled : false
    };
    await updateDoc(userRef, safeData);
    return { uid, ...safeData };
  }
}

// Eliminar usuario (opcional)
export async function eliminarUsuario(uid) {
  const userRef = doc(db, "users", uid);
  await deleteDoc(userRef);
}

// Desactivar o activar usuario
export async function desactivarUsuario(uid, activar = false) {
  try {
    const response = await fetchEndpoint(ENDPOINTS.USERS.UPDATE, {
      method: 'PUT',
      body: JSON.stringify({ uid, isActive: !activar ? false : true })
    });
    return response;
  } catch (error) {
    console.error('Error al desactivar/activar usuario:', error);
    // Fallback a Firestore si el endpoint falla
    const userRef = doc(db, "users", uid);
    await updateDoc(userRef, { isActive: activar ? true : false });
    return { uid, isActive: activar ? true : false };
  }
}

// Eliminar usuario de Authentication y Firestore vía endpoint seguro
export async function eliminarUsuarioTotal(uid) {
  try {
    const response = await fetchEndpoint(ENDPOINTS.USERS.DELETE, {
      method: 'POST',
      body: JSON.stringify({ uid })
    });
    return response;
  } catch (error) {
    console.error('Error al eliminar usuario:', error);
    throw error;
  }
} 